export type FiloEnv = {
  apiUrl: string;
  awsRegion: string;
  cognitoDomain: string;
  cognitoClientId: string;
  redirectUri: string;
  logoutUri: string;
};

export type FiloEnvSource = Pick<ImportMetaEnv,
  | 'VITE_API_URL'
  | 'VITE_AWS_REGION'
  | 'VITE_COGNITO_DOMAIN'
  | 'VITE_COGNITO_CLIENT_ID'
  | 'VITE_COGNITO_REDIRECT_URI'
  | 'VITE_COGNITO_LOGOUT_URI'
>;

let cachedEnv: FiloEnv | null = null;

const required = (source: FiloEnvSource, name: keyof FiloEnvSource): string => {
  const value = source[name]?.trim();
  if (!value) throw new Error(`環境変数 ${name} が設定されていません`);
  return value;
};

const isLocalHost = (hostname: string) => hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

const secureUrl = (name: string, rawValue: string, allowPath: boolean): string => {
  let parsed: URL;
  try {
    parsed = new URL(rawValue);
  } catch {
    throw new Error(`${name} は有効なURLではありません`);
  }
  if (parsed.protocol !== 'https:' && !(parsed.protocol === 'http:' && isLocalHost(parsed.hostname))) {
    throw new Error(`${name} はHTTPSで指定してください（localhostのみHTTPを使用できます）`);
  }
  if (!allowPath && parsed.pathname !== '/' && parsed.pathname !== '') {
    throw new Error(`${name} にパスは指定できません`);
  }
  if (allowPath && parsed.pathname === '/' && !parsed.search && !parsed.hash) {
    return parsed.origin;
  }
  return allowPath ? parsed.toString() : parsed.origin;
};

export const parseFiloEnv = (source: FiloEnvSource, origin: string): FiloEnv => {
  const originUrl = secureUrl('window.location.origin', origin, false);
  const rawDomain = required(source, 'VITE_COGNITO_DOMAIN');
  const cognitoDomain = secureUrl(
    'VITE_COGNITO_DOMAIN',
    /^https?:\/\//i.test(rawDomain) ? rawDomain : `https://${rawDomain}`,
    false,
  );
  return {
    apiUrl: secureUrl('VITE_API_URL', required(source, 'VITE_API_URL'), true).replace(/\/+$/, ''),
    awsRegion: required(source, 'VITE_AWS_REGION'),
    cognitoDomain,
    cognitoClientId: required(source, 'VITE_COGNITO_CLIENT_ID'),
    redirectUri: secureUrl(
      'VITE_COGNITO_REDIRECT_URI',
      source.VITE_COGNITO_REDIRECT_URI?.trim() || `${originUrl}/auth/callback`,
      true,
    ),
    logoutUri: secureUrl(
      'VITE_COGNITO_LOGOUT_URI',
      source.VITE_COGNITO_LOGOUT_URI?.trim() || `${originUrl}/`,
      true,
    ),
  };
};

export const getEnv = (): FiloEnv => {
  if (cachedEnv) return cachedEnv;
  const origin = typeof window === 'undefined' ? 'http://localhost:5173' : window.location.origin;
  cachedEnv = parseFiloEnv(import.meta.env, origin);
  return cachedEnv;
};
